import React, { useState, memo } from 'react';
import { useImmer } from 'use-immer';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import { BasicMemberInfo } from './Types';
import { apiGetEventorMembers } from './Api';
import { reportError } from './CallCenter';
import { CourierBand, useCourier } from './Courier';


const MemberRow = ({member}: {member: BasicMemberInfo}) => {
    const [importStatus, setImportStatus] = useState("not_imported")
    const courier = useCourier()

    const onImportClick = (eventor_person_id: string) => {
        setImportStatus("in_progress")
        // Reading Eventor's member page goes through the user's own browser now (Courier.tsx), and
        // the job has to last as long as the stream — the server fetches per member as it goes.
        void courier.run(() => new Promise<void>(done => {
        const url = new URL('../imports/importeventormember', document.baseURI)
        url.searchParams.append('eventor_person_id', eventor_person_id)

        const serverEventSource = new EventSource(url)
        serverEventSource.addEventListener("finished", _serverEvent => {
            serverEventSource.close()
            setImportStatus("imported")
            done()
        })
        serverEventSource.addEventListener("importError", serverEvent => {
            serverEventSource.close()
            const data = JSON.parse(serverEvent.data)
            setImportStatus("not_imported")
            done()
            reportError({ status: 502, code: "import_failed", message: data.message })
        })
        serverEventSource.onerror = () => {
            serverEventSource.close()
            setImportStatus("not_imported")
            done()
            reportError({ status: 0, code: "import_connection_lost", message: "Anslutningen till importen bröts." })
        }
        }))
    }

    const sx = (importStatus === "imported") ? {color: 'text.disabled'} : {}
    const buttonEnabled = importStatus === "not_imported"
    return <>
        <Box sx={sx}>{member.eventor.last_modified}</Box>
        <Box sx={sx}>{member.eventor.id.given_names.join(' ')}</Box>
        <Box sx={sx}>{member.eventor.id.family_name}</Box>
        <Box sx={sx}>{member.eventor.email_address}</Box>
        <Box sx={sx}>{member.eventor.mobile_phone_number}</Box>
        <Box sx={sx}><Button variant='contained' disabled={!buttonEnabled} onClick={() => onImportClick(member.eventor.person_id)}>Importera</Button></Box>
        { importStatus === "in_progress" && <Backdrop sx={{ color: '#fff', zIndex: 1000 }} open={true}><CircularProgress color="inherit" /></Backdrop> }
    </>
}

const ImportResult = ({members}: {members: BasicMemberInfo[] | null}) => {
    if (members === null)
        return <CircularProgress/>
    
    const newMembers = members.filter(member => member.visma_customer_id === null)
    const sortedMembers = newMembers.toSorted((a, b) => a.eventor.last_modified.localeCompare(b.eventor.last_modified))
    return members.length === 0 ? <div>Inga nya medlemmar</div> :
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, max-content)',
            rowGap: '0.5rem',
            columnGap: '1.5rem',
            alignItems: 'center'
        }}>
            <div><strong>Senast ändrad på Eventor</strong></div>
            <div><strong>Förnamn</strong></div>
            <div><strong>Efternamn</strong></div>
            <div><strong>E-adress</strong></div>
            <div><strong>Telefonnummer</strong></div>
            <div/>
            {sortedMembers.map(member =><MemberRow key={member.eventor.person_id} member={member}/>)}
        </div>
}

const EventorMemberImport = memo(() => {
    const [buttonClicked, setButtonClicked] = useState(false)
    const [members, setMembers] = useImmer<BasicMemberInfo[] | null>([])

    const handleClick = () => {
        setButtonClicked(true)
        setMembers(null)
        apiGetEventorMembers().then(setMembers)
    }
    
    return (
        <div style={{ display: 'flex', flexFlow: 'column nowrap', alignItems: 'flex-start', gap: '0.5rem' }}>
            <div style={{ width: '100%', maxWidth: '60rem' }}><CourierBand/></div>
            <Button variant="outlined" disabled={members === null} onClick={handleClick}>Lista medlemmar i Eventor som saknas i Visma</Button>
            {buttonClicked &&
                <ImportResult
                    members={members}
                />
            }        
        </div>
    )
})


export default EventorMemberImport
